import React, { useContext, createContext, useEffect, useMemo } from 'react';

import { useCurrentProject } from '@adsk/alloy-provider';
import { useLocalState } from '@adsk/alloy-react-helpers';

import useContract from './useContract';
import usePermission from './usePermission';
import useTerminologies from './useTerminologies';
import {
  getLocalStatePermissionsKey,
  PERMISSION_LEVEL,
  MAPPING_PERMISSION,
} from '../helpers/consts';

const CostContext = createContext({});
const useCostContext = () => useContext(CostContext);

const CostProvider = ({ children }) => {
  const currentProject = useCurrentProject();
  const projectId = useMemo(() => currentProject?.id, [currentProject]);

  const productAllowed = useMemo(
    () =>
      projectId &&
      currentProject?.products.some((product) => product.key === 'cost'),
    [currentProject, projectId],
  );

  const { terminologies } = useTerminologies(productAllowed);
  const { permission } = usePermission(productAllowed);

  const isPcoCollaborateCreate = useMemo(
    () =>
      productAllowed &&
      permission?.find((item) => item.resourceType === MAPPING_PERMISSION.pco)
        ?.permissionLevel === PERMISSION_LEVEL.COLLABORATE_CREATE,
    [productAllowed, permission],
  );

  const { contracts } = useContract(isPcoCollaborateCreate);

  const permissionKey = useMemo(
    () => getLocalStatePermissionsKey(projectId),
    [projectId],
  );
  const [, setPermission] = useLocalState(permissionKey, null);

  useEffect(() => {
    permission && setPermission(permission);
    return () => {
      try {
        localStorage.removeItem(permissionKey);
      } catch (e) {
        console.error(e);
      }
    };
  }, [permission, permissionKey, setPermission]);

  return (
    <CostContext.Provider
      value={{ terminologies, contracts, isPcoCollaborateCreate }}
    >
      {children}
    </CostContext.Provider>
  );
};

CostProvider.displayName = 'CostProvider';

export { useCostContext, CostProvider, CostContext };
